<template>
  <div>
    <filter-manager
      v-model="filter.model"
      :filters="filtersHeader"
      :display-advanced="false"
      class="filter-manager_extended"
      search-container-class="search-btn"
      @center="val => getMunicipalities(val?.id)"
      @municipality="val => getSchools(val?.id)"
      @search="() => onSearchHandler()"
    />
    <custom-separator
      text="Resultado de Libros"
      class="my-4 custom-separator-line"
    />

    <div v-if="hasResults">
      <result-row
        v-for="(item, index) in results"
        inventory-flow
        from-book-requests
        :key="index"
        :item="item"
        :order="order"
        :out-of-my-inventory="isOutOfCenter"
        :editable="inventoryEditable"
        @open="handlerOpen"
        @close="handlerClose"
        @refresh="refresh"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        :page-size="pagination.pageSize"
        @pagination-go-page="onPageChanged"
      />
    </div>

    <custom-result-not-found
      v-else
      class="mt-4 row pt-3"
      text="No se han encontrado resultados de libros."
    />
  </div>
</template>

<script>
import CustomSeparator from "custom-separator";
import CustomResultNotFound from "custom-result-not-found";
import FilterManager from "FilterManager";
import ResultRow from "../../inventoryCatalog/Management/components/ResultRow.vue";
import storeMixin from "@/mixin/storeGenerator";
import locationService from "@/services/api/location.api";
import inventoryCatalogService from "@/services/api/inventorycatalog.api";
import { studentBookStatus } from "@/utils/constants/book/studentBookStatus.js";
import Pagination from "pagination";
const DEFAULT_PAGE_SIZE = 5;

export default {
  name: "BookSearch",
  mixins: [storeMixin],
  components: {
    CustomSeparator,
    CustomResultNotFound,
    FilterManager,
    Pagination,
    ResultRow
  },
  props: {
    order: {
      type: Object,
      default: () => ({})
    },
    isOutOfCenter: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      results: [],
      filter: {
        model: {
          publisher: null,
          author: null,
          center: null,
          municipality: null,
          school: null
        },
        options: {
          publishers: [],
          authors: [],
          centers: [],
          municipalities: [],
          schools: []
        }
      },
      pagination: {
        count: 0,
        currentPage: 1,
        pageCount: 1,
        totalItemCount: 0,
        pageSize: DEFAULT_PAGE_SIZE
      }
    };
  },
  async mounted() {
    await this.initData();
  },
  computed: {
    hasResults() {
      return Boolean(this.results.length);
    },
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    inventoryEditable() {
      return [
        studentBookStatus.PendienteEntregaEscuela,
        studentBookStatus.PendienteEntregaMaestro
      ].includes(this.order?.status.id);
    },
    filtersHeader() {
      return [
        {
          name: "publisher",
          placeholder: "Casa Editorial",
          component: "CustomDropDown",
          value: this.filter.model.publisher,
          options: this.filter.options.publishers,
          class: "col-12 col-sm-6 col-md",
          fieldName: "name",
          fieldKey: "id"
        },
        {
          name: "author",
          placeholder: "Autor",
          component: "CustomDropDown",
          value: this.filter.model.author,
          options: this.filter.options.authors,
          class: "col-12 col-sm-6 col-md",
          fieldName: "name",
          fieldKey: "id"
        },
        {
          name: "center",
          placeholder: "Centro",
          component: "CustomDropDown",
          value: this.filter.model.center,
          options: this.filter.options.centers,
          class: "col-12 col-sm-6 col-md",
          fieldName: "name",
          fieldKey: "id",
          clearable: true,
          visible: this.isOutOfCenter
        },
        {
          name: "municipality",
          placeholder: "Municipio",
          component: "CustomDropDown",
          value: this.filter.model.municipality,
          options: this.filter.options.municipalities,
          class: "col-12 col-sm-6 col-md",
          fieldName: "name",
          fieldKey: "id",
          clearable: true
        },
        {
          name: "school",
          placeholder: "Escuela",
          component: "CustomDropDown",
          value: this.filter.model.school,
          options: this.filter.options.schools,
          class: "col-12 col-sm-6 col-md",
          fieldName: "name",
          fieldKey: "id",
          clearable: true
        }
      ];
    },
    catalogId() {
      return this.order?.book?.id;
    },
    center() {
      return this.order?.center;
    }
  },
  methods: {
    async initData() {
      this.results = [];
      this.resetFilters();
      await Promise.all([
        this.getInventoryManagementFilters(),
        this.getCenters()
      ]);
      await this.onSearchHandler();
    },
    async onSearchHandler(currentPage = 1) {
      if (!this.catalogId || !this.order?.id) return;

      const {
        center,
        publisher,
        author,
        municipality,
        school
      } = this.filter.model;

      const params = {
        centerId: this.center?.id,
        publisherId: publisher?.id,
        authorId: author?.id,
        municipalityId: municipality?.id,
        gradeId: this.order?.grade.id,
        readingMedium: this.order?.readingMedium.name,
        schoolId: school?.id,
        pageIndex: currentPage,
        pageSize: this.pagination.pageSize
      };

      if (this.isOutOfCenter) {
        params.centerId = center?.id;
        params.excludeCenterId = this.center?.id;
      }

      const { data } = await inventoryCatalogService.getSearchBooks(
        this.catalogId,
        this.order?.id,
        params
      );

      this.results = data.data.map(x => ({ ...x, isOpen: false }));

      this.pagination = {
        pageSize: DEFAULT_PAGE_SIZE,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count
      };
    },
    async getCenters() {
      if (this.isOutOfCenter) {
        const { data } = await locationService.getCenters({
          academicYearId: this.academicYearId
        });
        this.filter.options.centers = data;

        if (data.length === 1) {
          this.filter.model.center = data[0];
          this.getMunicipalities(data[0].id);
        }
      } else this.getMunicipalities(this.center?.id);
    },
    async getMunicipalities(centerId) {
      this.filter.model.municipality = null;
      this.filter.options.municipalities = [];
      this.filter.model.school = null;
      this.filter.options.schools = [];

      if (!centerId) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId
      });

      this.filter.options.municipalities = data;

      if (data.length === 1) {
        this.filter.model.municipality = data[0];
        this.getSchools(data[0].id);
      }
    },
    async getSchools(municipalityId) {
      this.filter.model.school = null;
      this.filter.options.schools = [];

      if (!municipalityId) return;

      const { data } = await locationService.getSchools({
        academicYearId: this.academicYearId,
        municipalityId
      });

      this.filter.options.schools = data;
      if (data.length === 1) this.filter.model.school = data[0];
    },
    async handlerOpen(item) {
      this.results
        .filter(x => x.id !== item.id && x.isOpen)
        .forEach(x => (x.isOpen = false));

      item.isOpen = true;
    },
    handlerClose(item) {
      item.isOpen = false;
    },
    async refresh() {
      await this.onSearchHandler(this.pagination.currentPage);
      this.$emit("refresh");
    },
    async getInventoryManagementFilters() {
      const {
        data
      } = await inventoryCatalogService.getInventoryManagementFilters(
        this.catalogId
      );
      this.filter.options.authors = data.authors;
      this.filter.options.publishers = data.publishers;
    },
    resetFilters() {
      this.filter.model.publisher = null;
      this.filter.options.publishers = [];
      this.filter.model.author = null;
      this.filter.options.authors = [];
      this.filter.model.center = null;
      this.filter.options.centers = [];
      this.filter.model.municipality = null;
      this.filter.options.municipalities = [];
      this.filter.model.school = null;
      this.filter.options.schools = [];
    },
    async onPageChanged(currentPage) {
      this.pagination.currentPage = currentPage;
      await this.onSearchHandler(currentPage);
    }
  },
  watch: {
    isOutOfCenter() {
      this.initData();
    }
  }
};
</script>

<style scoped>
@media (min-width: 992px) {
  ::v-deep .search-btn {
    flex: 0 0 16.66666667% !important;
    max-width: 16.66666667% !important;
  }
}
</style>
